import { motion } from "framer-motion";
import { Activity, Users, Target, FileText, Eye, Gauge, Zap, TrendingUp } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const STAT_ICONS = { agenti: Users, attivita: Activity, lead: Target, preventivi: FileText };
const FEATURE_ICONS = { visibilita: Eye, dati: Gauge, decisioni: Zap, risultati: TrendingUp };

const Xos = () => {
  const { t } = useLanguage();
  return (
  <section className="py-24 lg:py-32 relative" data-testid="xos-section">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_80%,rgba(0,240,255,0.08),transparent_50%)] pointer-events-none" />
    <div className="max-w-7xl mx-auto px-6 lg:px-12 relative">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-5"
        >
          <p className="text-xs font-bold tracking-[0.3em] uppercase text-neon-cyan mb-4">{t.xos.overline}</p>
          <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
            {t.xos.titleA} <span className="neon-text">{t.xos.titleNeon}</span>
          </h2>
          <p className="mt-6 text-base lg:text-lg text-white/70 mb-8">
            {t.xos.subtitle}
          </p>
          <ul className="grid grid-cols-2 gap-4">
            {t.xos.features.map((f) => {
              const FIcon = FEATURE_ICONS[f.id];
              return (
              <li key={f.id} className="flex items-center gap-3 text-sm text-white/80" data-testid={`xos-feature-${f.id}`}>
                <span className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-neon-cyan shrink-0">
                  <FIcon size={16} />
                </span>
                {f.text}
              </li>
              );
            })}
          </ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="lg:col-span-7"
        >
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-white/40 mb-3" data-testid="xos-demo-badge">{t.xos.demoBadge}</p>
          <div className="glass-panel spotlight overflow-hidden rounded-2xl p-6 sm:p-8" data-testid="xos-dashboard">
            <div className="flex items-center justify-between mb-6">
              <p className="font-display font-bold">{t.xos.overview}</p>
              <span className="text-xs text-white/50 border border-white/10 rounded-full px-3 py-1">{t.xos.today}</span>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {t.xos.stats.map((s) => {
                const SIcon = STAT_ICONS[s.id];
                return (
                <div key={s.id} className="bg-white/5 border border-white/10 rounded-xl p-4" data-testid={`xos-stat-${s.id}`}>
                  <SIcon size={16} className="text-neon-cyan mb-2" />
                  <p className="font-display text-2xl font-bold">{s.value}</p>
                  <p className="text-xs text-white/60">{s.label}</p>
                  <p className="text-xs text-neon-cyan mt-1">{s.delta}</p>
                </div>
                );
              })}
            </div>
            <p className="text-xs font-bold tracking-widest uppercase text-white/50 mb-4">{t.xos.goalsTitle}</p>
            <div className="flex flex-col gap-4">
              {t.xos.goals.map((g) => (
                <div key={g.label}>
                  <div className="flex justify-between text-xs text-white/70 mb-2">
                    <span>{g.label}</span>
                    <span>{g.note || `${g.pct}%`}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${g.pct}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 1.2, ease: "easeOut" }}
                      className="h-full rounded-full bg-gradient-to-r from-neon-cyan to-neon-violet"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
  );
};

export default Xos;
